// Credit at the moment a brand chooses a creator (Dani, 2026-10-14): sending a
// campaign costs nothing, and each creator picked, or accepting an invite,
// sets aside their own videos (data/campaign-credit.ts). An invite needs free
// credit for itself and for every invite still waiting, as each may take a
// spot. When the free credit does not cover it, this dialog says what it
// costs, what is free and what to add, and sends an Admin to Billing with the
// amount ready and a way back to the campaign; a Manager is told to ask an Admin.

import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {useNavigate} from 'react-router-dom';
import type {BrandCampaign} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';
import {BrandApiError} from './api';
import {money} from './format';
import {useBrand} from './store';

type Verb = 'pick' | 'invite';

/**
 * What one more creator costs on the campaign, and a check before a pick or an
 * invite: `covered` opens the dialog instead when the credit falls short, and
 * `refused` does the same for the server's `credit_required`.
 */
export function useCreditGate(campaign: BrandCampaign, tab: 'applicants' | 'invite') {
  const {data, reload} = useBrand();
  const [who, setWho] = useState<{name: string; verb: Verb; waiting: number} | null>(null);
  const perCreator = campaign.credit?.open ? campaign.credit.per_creator_cents : 0;
  const free = Math.max(0, data?.billing.available_cents ?? 0);
  /** `waiting`: invites still out, each of which may take a spot (for an invite only). */
  function covered(name: string, verb: Verb, waiting = 0): boolean {
    if (perCreator > 0 && free < perCreator * (waiting + 1)) { setWho({name, verb, waiting}); return false; }
    return true;
  }
  async function refused(error: unknown, name: string, verb: Verb, waiting = 0): Promise<boolean> {
    if (!(error instanceof BrandApiError) || error.code !== 'credit_required') return false;
    await reload(true);
    setWho({name, verb, waiting});
    return true;
  }
  const dialog = who && data ? <NeedCreditDialog campaign={campaign} tab={tab} name={who.name} verb={who.verb} waiting={who.waiting}
    perCreator={perCreator} free={free} admin={data.me.role === 'admin'} onClose={() => setWho(null)} /> : null;
  return {perCreator, free, covered, refused, dialog};
}

function NeedCreditDialog({campaign, tab, name, verb, waiting, perCreator, free, admin, onClose}: {
  campaign: BrandCampaign; tab: 'applicants' | 'invite'; name: string; verb: Verb; waiting: number;
  perCreator: number; free: number; admin: boolean; onClose(): void;
}) {
  const navigate = useNavigate();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const needed = Math.max(0, perCreator * (waiting + 1) - free);
  const monthly = campaign.cadence === 'recurring' ? ' a month' : '';
  const videos = `${campaign.videos_per_creator} ${campaign.videos_per_creator === 1 ? 'video' : 'videos'}${monthly}`;
  const back = `/campaigns/${campaign.id}?tab=${tab}`;
  // On the page's root, so nothing the campaign page stacks (or the assistant's button) sits above it.
  return createPortal(<div className="switch-layer">
    <div className="switch-backdrop" onClick={onClose} />
    <div className="card switch-dialog credit-dialog" role="dialog" aria-modal="true" aria-labelledby="credit-title">
      <button className="switch-close" type="button" onClick={onClose} aria-label="Close"><Icon name="x" size={18} /></button>
      <p className="eyebrow">Add credit</p>
      {needed ? <h2 id="credit-title">Add {money(needed, 'USD')} to {verb === 'pick' ? `bring ${name} in` : `invite ${name}`}</h2>
        : <h2 id="credit-title">Your credit covers {name} now</h2>}
      <dl className="credit-math">
        <div><dt>{name}’s {videos}</dt><dd>{money(perCreator, 'USD')}</dd></div>
        {waiting ? <div><dt>{waiting} {waiting === 1 ? 'invite' : 'invites'} still waiting</dt><dd>{money(perCreator * waiting, 'USD')}</dd></div> : null}
        <div><dt>Free credit now</dt><dd>{money(free, 'USD')}</dd></div>
        {needed ? <div className="credit-math-total"><dt>To add</dt><dd>{money(needed, 'USD')}</dd></div> : null}
      </dl>
      <p className="switch-note"><Icon name="info" size={16} />{verb === 'pick'
        ? 'Each creator you pick sets aside their videos from your credit.'
        : 'A creator who accepts an invite sets aside their videos from your credit, so your free credit covers every invite that is still waiting.'} You pay only for the videos you approve, and what is not used comes back to your credit.</p>
      <div className="switch-actions">
        {!needed ? <button className="btn btn-g" type="button" onClick={onClose}>{verb === 'pick' ? 'Pick again' : 'Invite again'}</button>
          : admin ? <>
            <button className="btn btn-light" type="button" onClick={onClose}>Not now</button>
            <button className="btn btn-g" type="button" onClick={() => navigate(`/billing?need=${needed}&back=${encodeURIComponent(back)}`)}>Add {money(needed, 'USD')}<Icon name="arrowRight" size={16} /></button>
          </> : <>
            <p className="credit-ask">Only an Admin of your team adds credit. Ask one of them to add {money(needed, 'USD')}.</p>
            <button className="btn btn-light" type="button" onClick={onClose}>OK</button>
          </>}
      </div>
    </div>
  </div>, document.body);
}
